import React, { useEffect, useState } from 'react'
import moment from 'moment'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography
} from '@material-ui/core'
import { useHistory, useLocation } from 'react-router-dom'
import adminsSlice, { deleteUserByUserId, fetchAllUsers, resendActivationEmail } from 'store/Admins'
import { useDispatch, useSelector } from 'react-redux'
import BasicTable from 'components/BasicTable'
import ResourceNotFound from 'components/ResourceNotFound'
import Wrapper from 'components/Wrapper'
import { EditOutlined, Delete, MailOutline } from '@material-ui/icons'
import getTableOptions from 'utils/getTableOptions'
import ConfirmModal from 'components/ConfirmationModal'
import { caseInsensitiveSort } from 'utils'
import { getConferenceRole, getConferenceStatus, isAssignedToConference } from 'utils/conference'

const tableHead = ['Full Name', 'Email', 'Role', 'Status', 'Invited', 'Expires', 'Actions']

const formatInviteDate = (ms) => (ms ? moment(ms).format('MMM D, YYYY h:mm A') : '—')

const isInviteExpired = (admin) =>
  Boolean(admin.inviteExpiresAt) && Date.now() > admin.inviteExpiresAt

const getRoleChipColor = (role) => {
  switch (role) {
    case 'owner':
      return 'primary'
    case 'admin':
      return 'default'
    default:
      return 'default'
  }
}

const getStatusChipProps = (admin, confFilter) => {
  if (admin.mustChangePassword) {
    return { label: 'Pending Activation', backgroundColor: '#f59e0b' }
  }
  const status = getConferenceStatus(admin, confFilter)
  return status === 'in-active'
    ? { label: 'Inactive', backgroundColor: '#f44336' }
    : { label: 'Active', backgroundColor: '#4caf50' }
}

const formatTableData = (admins, confFilter) =>
  admins.map((admin) => {
    const role = getConferenceRole(admin, confFilter)
    const { label, backgroundColor } = getStatusChipProps(admin, confFilter)
    return [
      admin.fullName,
      admin.email,
      <Chip label={role} color={getRoleChipColor(role)} size="small" />,
      <Chip label={label} size="small" style={{ backgroundColor, color: '#fff' }} />,
      formatInviteDate(admin.inviteSentAt),
      formatInviteDate(admin.inviteExpiresAt),
      <ActionButtons admin={admin} confFilter={confFilter} />
    ]
  })

const ActionButtons = ({ admin, confFilter }) => {
  const location = useLocation()
  const history = useHistory()
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.auth)
  const [deletedId, setDeletedId] = useState('')
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [resending, setResending] = useState(false)
  const [openResendModal, setOpenResendModal] = useState(false)

  const handleOpenDeleteModal = (id) => {
    setOpenDeleteModal(true)
    setDeletedId(id)
  }

  const handleDeleteUser = async () => {
    dispatch(deleteUserByUserId({ uid: deletedId, confId: confFilter }))
  }

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false)
    setDeletedId('')
  }

  const handleResendActivation = async () => {
    setResending(true)
    await dispatch(resendActivationEmail({ uid: admin.uid, callerUid: user?.id }))
    setResending(false)
    setOpenResendModal(false)
  }

  const expired = isInviteExpired(admin)

  return (
    <Box sx={{ display: 'flex', gap: '0.5rem' }}>
      <ConfirmModal
        dialogTitle="Delete Conference User"
        dialogText="Do you really want to delete this conference user?"
        open={openDeleteModal}
        closeModal={handleCloseDeleteModal}
        handleConfirm={handleDeleteUser}
      />
      {admin.mustChangePassword && (
        <>
          <ConfirmModal
            dialogTitle="Resend Activation Email"
            dialogText={`This will invalidate the current activation link and send ${
              admin.fullName || 'this user'
            } a brand-new temporary password and link. Continue?`}
            open={openResendModal}
            inProgress={resending}
            closeModal={() => setOpenResendModal(false)}
            handleConfirm={handleResendActivation}
          />
          <Tooltip
            title={
              expired
                ? 'Resend Activation Email'
                : `Invitation still valid until ${formatInviteDate(
                    admin.inviteExpiresAt
                  )} — resend becomes available once it expires`
            }>
            <span>
              <IconButton size="small" disabled={!expired} onClick={() => setOpenResendModal(true)}>
                <MailOutline />
              </IconButton>
            </span>
          </Tooltip>
        </>
      )}
      <Tooltip title="Edit">
        <IconButton
          size="small"
          onClick={() =>
            history.push({
              pathname: `${location.pathname}/edit/${admin.uid}`,
              search: `?conferenceId=${confFilter}`
            })
          }>
          <EditOutlined />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton size="small" onClick={() => handleOpenDeleteModal(admin.uid)}>
          <Delete />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

const AdminsListing = () => {
  const classes = useStyles()
  const options = getTableOptions()
  const history = useHistory()
  const dispatch = useDispatch()
  const location = useLocation()
  const { admins, inProgress, confFilter, currentPage, pageSize } = useSelector(
    (state) => state.admins
  )
  const { user } = useSelector((state) => state.auth)

  const { conferences, inProgress: confProgress } = useSelector((state) => state.conference)
  const activeConferences = conferences?.filter((row) => row.isActive === true && !row.isArchived)
  const orderedConferences = caseInsensitiveSort(activeConferences, 'confName')

  useEffect(() => {
    if (confFilter) {
      let inActive = false
      conferences.map((conf) => {
        if (conf.id === confFilter && (conf.isActive === false || conf.isArchived === true)) {
          inActive = true
        }
      })
      if (inActive && !confProgress) {
        dispatch(adminsSlice.actions.setConfFilter(orderedConferences[0]?.id))
      }
    }
  }, [confProgress, confFilter])

  useEffect(() => {
    if (!confFilter && !confProgress) {
      dispatch(adminsSlice.actions.setConfFilter(orderedConferences[0]?.id))
    }
  }, [confProgress, confFilter])

  // Guards against showing the previously selected conference's users while the fetch
  // for the newly selected one is still in flight.
  const filteredAdmins = admins?.filter((admin) => isAssignedToConference(admin, confFilter))

  useEffect(() => {
    dispatch(adminsSlice.actions.setIsCreatedOrUpdated(false))
    if (confFilter) {
      dispatch(fetchAllUsers({ conferenceId: confFilter, currentUserRole: user?.role }))
    }
  }, [dispatch, confFilter, user?.role])

  return (
    <Box>
      <Box className={classes.headBar}>
        <Typography variant="h5" className={classes.headerTitle}>
          Conference Users
        </Typography>
        <Box className={classes.actions}>
          <Box className={classes.dateFilterDropdown}>
            <Typography className={classes.dateFilterLabel}>Conference: </Typography>
            {confProgress ? (
              <CircularProgress size={25} />
            ) : (
              <FormControl fullWidth variant="outlined" size="small" className={classes.select}>
                <Select
                  value={confFilter}
                  onChange={(event) =>
                    dispatch(adminsSlice.actions.setConfFilter(event.target.value))
                  }>
                  {orderedConferences.map((conf) => (
                    <MenuItem value={conf?.id} key={conf?.id}>
                      {conf?.confName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => {
              if (confFilter) {
                history.push({
                  pathname: `${location.pathname}/new`,
                  search: `?conferenceId=${confFilter}`
                })
              }
            }}>
            Create Conference User
          </Button>
        </Box>
      </Box>
      <Wrapper inProgress={inProgress}>
        <Paper variant="outlined" style={{ marginTop: '20px' }}>
          {filteredAdmins.length ? (
            <BasicTable
              data={formatTableData(filteredAdmins, confFilter)}
              columns={tableHead}
              options={{
                ...options,
                onChangePage: (page) => dispatch(adminsSlice.actions.setCurrentPage(page)),
                onChangeRowsPerPage: (size) => dispatch(adminsSlice.actions.setPageSize(size)),
                page: currentPage,
                rowsPerPage: pageSize
              }}
            />
          ) : (
            <ResourceNotFound message="No conference users available for selected conference" />
          )}
        </Paper>
      </Wrapper>
    </Box>
  )
}
export default AdminsListing

const useStyles = makeStyles({
  headBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  headerTitle: {
    fontWeight: 600,
    color: '#263238'
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  select: {
    minWidth: '200px'
  },
  dateFilterDropdown: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end'
  },
  dateFilterLabel: {
    width: '100%',
    fontSize: '1rem',
    textAlign: 'right',
    marginRight: '8px',
    fontWeight: 'bold'
  }
})
